/*
  <cbg-hero-slider> — hero slider web component.

  Swiper is NOT loaded with the page. This component watches its own position
  with an IntersectionObserver and injects the vendored Swiper assets
  (assets/swiper-bundle.min.js / .css, URLs passed via data attributes)
  only when the slider is ~400px from entering the viewport.

  Until Swiper initializes, CSS in section-hero-slider.css shows the first
  slide only — so a load failure degrades to a static hero image.
*/

if (!customElements.get('cbg-hero-slider')) {
  class CbgHeroSlider extends HTMLElement {
    /* Shared across instances — Swiper is fetched once even with several sliders on a page */
    static swiperPromise = null;

    static loadSwiper(jsUrl, cssUrl) {
      if (!CbgHeroSlider.swiperPromise) {
        /* Swiper initializes as soon as BOTH assets are ready — resolving on the
           script alone could run the first transition with unstyled slides */
        CbgHeroSlider.swiperPromise = new Promise((resolve, reject) => {
          if (window.Swiper) {
            resolve();
            return;
          }

          let pending = 2;
          const done = () => {
            pending -= 1;
            if (pending === 0) resolve();
          };
          const fail = () => {
            CbgHeroSlider.swiperPromise = null;
            reject(new Error('Swiper failed to load'));
          };

          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = cssUrl;
          link.onload = done;
          link.onerror = fail;
          document.head.appendChild(link);

          const script = document.createElement('script');
          script.src = jsUrl;
          script.onload = done;
          script.onerror = fail;
          document.head.appendChild(script);
        });
      }
      return CbgHeroSlider.swiperPromise;
    }

    connectedCallback() {
      this.swiperEl = this.querySelector('.cbg-hslider__swiper');
      this.slides = Array.from(this.querySelectorAll('.cbg-hslider__slide'));
      if (!this.swiperEl || this.slides.length === 0) return;

      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect();
            this.observer = null;
            this.initSlider();
          }
        },
        { rootMargin: '400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.swiper) {
        this.swiper.destroy(true, false);
        this.swiper = null;
      }
    }

    async initSlider() {
      try {
        await CbgHeroSlider.loadSwiper(this.dataset.swiperJs, this.dataset.swiperCss);
      } catch (error) {
        console.warn('cbg-hero-slider:', error.message);
        return; /* first slide stays visible via the pre-init CSS */
      }

      if (!this.isConnected || this.swiper) return;

      const hasMultipleSlides = this.slides.length > 1;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplayEnabled = this.dataset.autoplay === 'true' && hasMultipleSlides && !prefersReducedMotion;

      this.swiper = new window.Swiper(this.swiperEl, {
        slidesPerView: 1,
        spaceBetween: parseInt(this.dataset.gap, 10) || 0,
        loop: hasMultipleSlides,
        watchOverflow: true,
        autoplay: autoplayEnabled
          ? {
              delay: parseInt(this.dataset.autoplaySpeed, 10) || 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
        navigation: {
          prevEl: this.querySelector('.cbg-hslider__arrow--prev'),
          nextEl: this.querySelector('.cbg-hslider__arrow--next'),
        },
        pagination: {
          el: this.querySelector('.cbg-hslider__pagination'),
          clickable: true,
          renderBullet: (index, className) =>
            '<button type="button" class="' + className + ' cbg-hslider__bullet"><span class="visually-hidden">' +
            (index + 1) +
            '</span></button>',
        },
      });
    }

    onBlockSelect(event) {
      const slide = event.target.closest('.cbg-hslider__slide');
      if (!slide || !this.contains(slide) || !this.swiper) return;
      this.swiper.slideToLoop(this.slides.indexOf(slide));
      if (this.swiper.autoplay) this.swiper.autoplay.stop();
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target) || !this.swiper) return;
      if (this.dataset.autoplay === 'true' && this.swiper.autoplay) this.swiper.autoplay.start();
    }
  }

  customElements.define('cbg-hero-slider', CbgHeroSlider);
}
